export type Box = { x: number; y: number; w: number; h: number };
export type Rect = { left: number; top: number; width: number; height: number };

/**
 * Screen<->user mapping for an <svg viewBox> rendered with the *default*
 * preserveAspectRatio="xMidYMid meet".
 *
 * "meet" fits the whole viewBox inside the element using ONE uniform scale
 *   s = min(rect.w / box.w, rect.h / box.h)
 * and then centres the result, letterboxing the spare axis:
 *   offX = (rect.w - box.w * s) / 2, offY = (rect.h - box.h * s) / 2
 *
 * Scaling pixel deltas by box.w/rect.width (i.e. assuming a separate scale per
 * axis) is only right when the aspect ratios match, which they never do here —
 * fitBox() returns raw node bounds. Getting it wrong makes the graph drift away
 * from the cursor when zooming and move by the wrong amount when panning.
 *
 * Kept pure (box passed in, not read from the DOM) so a drag can keep using the
 * viewBox as it was at mousedown while the live DOM viewBox is already updated.
 */
export function userScale(box: Box, rect: Rect): number {
  return Math.min(rect.width / box.w, rect.height / box.h);
}

/** Client (viewport) coordinates -> user-space coordinates inside `box`. */
export function clientToUser(box: Box, rect: Rect, clientX: number, clientY: number): { x: number; y: number } {
  const s = userScale(box, rect);
  const offX = (rect.width - box.w * s) / 2;
  const offY = (rect.height - box.h * s) / 2;
  return {
    x: box.x + (clientX - rect.left - offX) / s,
    y: box.y + (clientY - rect.top - offY) / s,
  };
}

/** A pixel drag delta expressed in user-space units. */
export function pixelsToUser(box: Box, rect: Rect, dxPx: number, dyPx: number): { x: number; y: number } {
  const s = userScale(box, rect);
  return { x: dxPx / s, y: dyPx / s };
}

// --- self-check: run with `bun src/lib/viewbox.ts` (no test framework here).
// The guard keeps it out of the browser bundle path (import.meta.main is only
// truthy for a file bun executes directly).
export function __check__() {
  const box: Box = { x: -400, y: -300, w: 800, h: 600 }; // 4:3
  const rect: Rect = { left: 0, top: 0, width: 1000, height: 500 }; // 2:1 -> letterboxed left/right
  const s = userScale(box, rect); // min(1.25, 0.8333) = 0.8333
  if (Math.abs(s - 500 / 600) > 1e-9) throw new Error("scale");
  // centre of the element maps to the centre of the viewBox
  const c = clientToUser(box, rect, 500, 250);
  if (Math.abs(c.x - 0) > 1e-9 || Math.abs(c.y - 0) > 1e-9) throw new Error("centre");
  // 100 px right is 100/s user units, NOT 100 * box.w/rect.width (= 80)
  const d = pixelsToUser(box, rect, 100, 0);
  if (Math.abs(d.x - 120) > 1e-9) throw new Error("delta");
  return true;
}

if ((import.meta as { main?: boolean }).main) __check__();
